import { useState, useEffect, useCallback, useRef } from "react";
import { useAuth } from "@/App";
import { api, fetchCentersFromDB, isAdminUser } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { CostingEditDialog } from "@/components/menu-costing/CostingEditDialog";
import { DuplicatesDialog } from "@/components/menu-costing/DuplicatesDialog";
import { mcPost } from "@/components/menu-costing/api";
import { Lock, Search, ChevronDown, ChevronRight, Save, RefreshCw, Eye, EyeOff, Plus, Store, X, CheckCheck } from "lucide-react";

export default function MenuManagement() {
  const { session } = useAuth();
  const [centers, setCenters] = useState([]);
  const [selectedCenter, setSelectedCenter] = useState("");
  const [menuData, setMenuData] = useState(null);
  const [categories, setCategories] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [expandedCats, setExpandedCats] = useState(new Set());
  const [editedPrices, setEditedPrices] = useState({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [seeding, setSeeding] = useState(false);
  const [showUnavailable, setShowUnavailable] = useState(true);
  const [addItemOpen, setAddItemOpen] = useState(false);
  const [newItem, setNewItem] = useState({ name: "", category: "", base_price: 0, description: "", is_veg: true, serves: "" });
  const [applyScope, setApplyScope] = useState("ALL_INDIA");   // "ALL_INDIA" | "SELECT"
  const [applyCenters, setApplyCenters] = useState([]);          // codes when scope=SELECT
  // iter181 — availability chip / modal
  const [availSummary, setAvailSummary] = useState({});          // {name: {available_count, total_active_centers}}
  const [availModalItem, setAvailModalItem] = useState(null);   // item obj open in modal
  const [availRows, setAvailRows] = useState([]);                // centres for modal
  const [availLoading, setAvailLoading] = useState(false);
  const [availSaving, setAvailSaving] = useState(false);
  const [availDirty, setAvailDirty] = useState({});              // {code: {available?, price?}}
  const [costMeta, setCostMeta] = useState(null);                // iter306 — costing popup meta / permissions
  const [costItem, setCostItem] = useState(null);
  const [dupOpen, setDupOpen] = useState(false);
  const centreRef = useRef("");
  centreRef.current = selectedCenter;

  useEffect(() => {
    if (session?.token) {
      fetchCentersFromDB(session.token).then(c => {
        setCenters(c);
        if (c.length > 0) setSelectedCenter(prev => prev || c[0].code);
      });
      loadCategories();
      mcPost("meta", { token: session.token }).then(setCostMeta).catch(() => {});
    }
  }, [session?.token]);

  useEffect(() => {
    if (selectedCenter && session?.token) loadMenu();
  }, [selectedCenter]);

  const loadCategories = async () => {
    try {
      // Backend exposes POST /masters/{type}/list with a JSON body.
      // (Previously we called GET which returned 405 and the empty
      // ``catch`` silently swallowed it — leaving the dropdown empty.)
      const res = await api.post("/masters/menu_categories/list",
                                       { token: session.token });
      setCategories(res.data?.items || []);
    } catch (err) {
      toast.error("Failed to load menu categories: " +
                     (err.response?.data?.detail || err.message));
      setCategories([]);
    }
  };

  const loadMenu = useCallback(async () => {
    if (!selectedCenter || !session?.token) return;
    setLoading(true);
    try {
      const res = await api.get(`/masters/menu-items/by-center/${selectedCenter}?token=${session.token}`);
      if (res.data?.center !== String(centreRef.current).toUpperCase()) return;  // stale response for a previous centre
      setMenuData(res.data);
      setEditedPrices({});
      // Auto-expand all categories
      const cats = new Set((res.data?.items || []).map(i => i.category));
      setExpandedCats(cats);
      // iter181 — refresh availability summary in the background
      api.post("/masters/menu-items/availability-summary",
                 { token: session.token })
         .then(r => setAvailSummary(r.data?.summary || {}))
         .catch(() => {});
    } catch (err) {
      toast.error("Failed to load menu: " + (err.response?.data?.detail || err.message));
    } finally {
      setLoading(false);
    }
  }, [selectedCenter, session?.token]);

  const handlePriceChange = (itemName, field, value) => {
    setEditedPrices(prev => ({
      ...prev,
      [itemName]: { ...(prev[itemName] || {}), [field]: value }
    }));
  };

  const saveChanges = async () => {
    const byKey = Object.fromEntries((menuData?.items || []).map(i => [i.id || i.name, i]));
    const entries = Object.entries(editedPrices);
    const items = entries.map(([key, changes]) => ({
      id: byKey[key]?.id, name: byKey[key]?.name || key,
      price: changes.price !== undefined && changes.price !== "" ? parseFloat(changes.price) : undefined,
      available: changes.available,
    })).filter(i => i.price !== undefined || i.available !== undefined);
    const bases = entries.filter(([, c]) => c.base_price !== undefined && c.base_price !== "");
    if (items.length === 0 && bases.length === 0) { toast.info("No changes to save"); return; }
    if (items.some(i => i.price !== undefined && (isNaN(i.price) || i.price < 0))) { toast.error("Enter a valid centre price"); return; }
    setSaving(true);
    const errors = [];
    let okCount = 0;
    try {
      if (items.length) {
        const res = await api.post("/masters/menu-items/bulk-set-center-prices", { token: session.token, center_code: selectedCenter, items });
        okCount += res.data.updated;
        (res.data.not_found || []).forEach(n => errors.push(`${n}: not found`));
      }
      for (const [key, c] of bases) {
        try {
          await mcPost("base-price/save", { token: session.token, menu_item_id: byKey[key]?.id, price: parseFloat(c.base_price), reason: "Menu Management · base price edit" });
          okCount += 1;
        } catch (e) { errors.push(`${byKey[key]?.name}: ${e.message}`); }
      }
      if (errors.length) toast.error(`Saved ${okCount}; failed ${errors.length}: ${errors.join("; ")}`);
      else toast.success(`Saved ${okCount} change${okCount === 1 ? "" : "s"} for ${selectedCenter}`);
      setEditedPrices({});
      loadMenu();
    } catch (err) {
      toast.error("Save failed: " + (err.response?.data?.detail || err.message));
    } finally {
      setSaving(false);
    }
  };

  const seedMenuData = async () => {
    setSeeding(true);
    try {
      const res = await api.post("/masters/seed-menu-data", { token: session.token });
      toast.success(`Seed: ${res.data.items_created} new items added, ${res.data.items_skipped_existing ?? 0} existing items left unchanged`);
      loadMenu();
      loadCategories();
    } catch (err) {
      toast.error("Seed failed: " + (err.response?.data?.detail || err.message));
    } finally {
      setSeeding(false);
    }
  };

  const addNewItem = async () => {
    if (!newItem.name.trim() || !newItem.category) {
      toast.error("Name and Category are required");
      return;
    }
    if (applyScope === "SELECT" && applyCenters.length === 0) {
      toast.error("Please pick at least one centre or switch to ALL India.");
      return;
    }
    try {
      const price = parseFloat(newItem.base_price) || 0;
      await api.post("/masters/menu_items/create", {
        token: session.token,
        name: newItem.name.trim(),
        category: newItem.category,
        base_price: price,
        description: newItem.description,
        is_veg: newItem.is_veg,
        serves: newItem.serves,
        center_prices: {},
      });
      // iter180: apply to selected centres in ONE call (never creates
      // duplicate menu-item documents).
      const applyRes = await api.post(
        "/masters/menu-items/apply-to-centers", {
          token: session.token,
          item_name: newItem.name.trim(),
          centers: applyScope === "ALL_INDIA" ? "ALL_INDIA" : applyCenters,
          price, available: true,
        });
      const n = applyRes.data?.centers_applied || 0;
      toast.success(`Item "${newItem.name}" created and applied to ${n} centre${n === 1 ? "" : "s"}.`);
      setNewItem({ name: "", category: "", base_price: 0, description: "", is_veg: true, serves: "" });
      setApplyCenters([]); setApplyScope("ALL_INDIA");
      setAddItemOpen(false);
      loadMenu();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to create item");
    }
  };

  const toggleCat = (cat) => {
    setExpandedCats(prev => {
      const n = new Set(prev);
      n.has(cat) ? n.delete(cat) : n.add(cat);
      return n;
    });
  };

  // ---- iter181: Availability modal ----
  const openAvailModal = async (item) => {
    setAvailModalItem(item);
    setAvailDirty({});
    setAvailRows([]);
    setAvailLoading(true);
    try {
      const r = await api.post("/masters/menu-items/get-availability", {
        token: session.token, item_name: item.name, id: item.id,
      });
      setAvailRows(r.data?.centers || []);
    } catch (err) {
      toast.error("Failed to load centres: " + (err.response?.data?.detail || err.message));
      setAvailModalItem(null);
    } finally {
      setAvailLoading(false);
    }
  };

  const closeAvailModal = () => {
    if (Object.keys(availDirty).length > 0 &&
          !window.confirm("Discard unsaved centre changes?")) return;
    setAvailModalItem(null);
    setAvailRows([]);
    setAvailDirty({});
  };

  const setAvailField = (code, field, value) => {
    setAvailDirty(prev => ({
      ...prev, [code]: { ...(prev[code] || {}), [field]: value },
    }));
  };

  const getAvailFieldValue = (row, field) => {
    const d = availDirty[row.center_code] || {};
    if (d[field] !== undefined) return d[field];
    return row[field];
  };

  const bulkAvailToggle = (targetCenters, available) => {
    setAvailDirty(prev => {
      const n = { ...prev };
      for (const code of targetCenters) {
        n[code] = { ...(n[code] || {}), available };
      }
      return n;
    });
  };

  const saveAvailChanges = async () => {
    if (!availModalItem) return;
    const changes = Object.entries(availDirty).map(([code, patch]) => {
      const row = availRows.find(r => r.center_code === code) || {};
      const nextAvail = patch.available !== undefined
                            ? patch.available : row.available;
      const priceRaw = patch.price !== undefined ? patch.price : row.price;
      const priceNum = parseFloat(priceRaw);
      return {
        center_code: code,
        available: !!nextAvail,
        price: Number.isFinite(priceNum) ? priceNum : row.price,
      };
    });
    if (changes.length === 0) { toast.info("No changes"); return; }
    setAvailSaving(true);
    try {
      const r = await api.post("/masters/menu-items/set-availability", {
        token: session.token,
        item_name: availModalItem.name,
        id: availModalItem.id,
        changes,
        note: `Managed via Menu Availability Modal`,
      });
      const b = r.data || {};
      toast.success(`Saved ${b.updated} centre change${b.updated === 1 ? "" : "s"}. Now available in ${b.available_count}/${b.total_active_centers} centres.`);
      // refresh summary + current-centre menu
      setAvailSummary(prev => ({
        ...prev,
        [availModalItem.name]: {
          available_count: b.available_count,
          total_active_centers: b.total_active_centers,
        },
      }));
      setAvailModalItem(null);
      setAvailRows([]);
      setAvailDirty({});
      loadMenu();
    } catch (err) {
      toast.error("Save failed: " + (err.response?.data?.detail || err.message));
    } finally {
      setAvailSaving(false);
    }
  };

  if (!isAdminUser(session)) {
    return (
      <div className="text-center py-20" data-testid="menu-access-denied">
        <Lock className="w-16 h-16 mx-auto mb-4 text-muted-foreground/50" />
        <h2 className="text-2xl font-bold text-muted-foreground">Access Denied</h2>
        <p className="text-muted-foreground mt-2">Only Admin users can manage menus</p>
      </div>
    );
  }

  const items = menuData?.items || [];
  const filtered = items.filter(i => {
    if (!showUnavailable && !i.available) return false;
    if (searchTerm) {
      const q = searchTerm.toLowerCase();
      return i.name.toLowerCase().includes(q) || i.category.toLowerCase().includes(q);
    }
    return true;
  });

  // Group by category
  const grouped = {};
  filtered.forEach(i => {
    if (!grouped[i.category]) grouped[i.category] = [];
    grouped[i.category].push(i);
  });
  const catOrder = Object.keys(grouped).sort();

  const symbol = menuData?.symbol || "₹";
  const currency = menuData?.currency || "INR";
  const centerObj = centers.find(c => c.code === selectedCenter);
  const hasChanges = Object.keys(editedPrices).length > 0;

  return (
    <div className="space-y-4" data-testid="menu-management-page">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">Menu Management</h1>
          <p className="text-sm text-slate-500">Center-specific menu items and pricing (Admin only)</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button variant="outline" size="sm" onClick={seedMenuData} disabled={seeding} data-testid="seed-menu-btn">
            <RefreshCw className={`w-4 h-4 mr-1 ${seeding ? "animate-spin" : ""}`} /> {seeding ? "Seeding..." : "Seed Menu Data"}
          </Button>
          {costMeta?.permissions?.is_super_admin && (
            <Button variant="outline" size="sm" onClick={() => setDupOpen(true)} data-testid="fix-duplicates-btn">Fix Duplicates</Button>
          )}
          <Button variant="outline" size="sm" onClick={() => setAddItemOpen(!addItemOpen)} data-testid="add-item-btn">
            <Plus className="w-4 h-4 mr-1" /> Add Item
          </Button>
          {hasChanges && (
            <Button size="sm" onClick={saveChanges} disabled={saving} className="bg-green-600 hover:bg-green-700" data-testid="save-prices-btn">
              <Save className="w-4 h-4 mr-1" /> {saving ? "Saving..." : `Save ${Object.keys(editedPrices).length} Changes`}
            </Button>
          )}
        </div>
      </div>

      {/* Add Item Form */}
      {addItemOpen && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 space-y-3" data-testid="add-item-form">
          <h3 className="font-semibold text-blue-800">Add New Menu Item</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Input placeholder="Item Name" value={newItem.name} onChange={e => setNewItem(p => ({ ...p, name: e.target.value }))} data-testid="new-item-name" />
            <div>
              <select className="border rounded px-3 py-2 text-sm w-full"
                value={newItem.category}
                onChange={e => setNewItem(p => ({ ...p, category: e.target.value }))}
                data-testid="new-item-category">
                <option value="">Select Category</option>
                {categories.filter(c => c.is_active !== false).map(c => (
                  <option key={c.name} value={c.name}>{c.name}</option>
                ))}
              </select>
              {categories.length === 0 && (
                <p className="text-[11px] text-amber-700 mt-1" data-testid="no-menu-cats-msg">
                  No menu categories configured. Add one in Masters →
                  Menu Categories, then reopen this form.
                </p>
              )}
            </div>
            <Input type="number" placeholder="Base Price (INR)" value={newItem.base_price || ""} onChange={e => setNewItem(p => ({ ...p, base_price: e.target.value }))} data-testid="new-item-price" />
            <Input placeholder="Serves (e.g. 4 pcs)" value={newItem.serves} onChange={e => setNewItem(p => ({ ...p, serves: e.target.value }))} data-testid="new-item-serves" />
          </div>

          {/* iter180: Applicable Centres */}
          <div className="space-y-2 pt-2 border-t border-blue-200">
            <label className="text-sm font-semibold text-blue-900">
              Applicable Centres
            </label>
            <div className="flex gap-4 text-sm">
              <label className="flex items-center gap-1 cursor-pointer">
                <input type="radio" name="apply-scope" value="ALL_INDIA"
                  checked={applyScope === "ALL_INDIA"}
                  onChange={() => setApplyScope("ALL_INDIA")}
                  data-testid="apply-scope-all" />
                All Active India Centres
              </label>
              <label className="flex items-center gap-1 cursor-pointer">
                <input type="radio" name="apply-scope" value="SELECT"
                  checked={applyScope === "SELECT"}
                  onChange={() => setApplyScope("SELECT")}
                  data-testid="apply-scope-select" />
                Select Centres
              </label>
            </div>
            {applyScope === "SELECT" && (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-1 pt-1"
                data-testid="apply-centers-list">
                {centers.filter(c => (c.country || "India").toLowerCase() === "india"
                                       || !c.country).map(c => (
                  <label key={c.code}
                    className="flex items-center gap-1 text-xs bg-white border rounded px-2 py-1 cursor-pointer">
                    <input type="checkbox"
                      checked={applyCenters.includes(c.code)}
                      onChange={e => setApplyCenters(prev =>
                        e.target.checked
                          ? [...prev, c.code]
                          : prev.filter(x => x !== c.code))}
                      data-testid={`apply-center-${c.code}`} />
                    {c.code}
                  </label>
                ))}
              </div>
            )}
            <p className="text-[11px] text-blue-800/70">
              International centres are excluded — add them explicitly if needed. Existing per-centre prices are preserved.
            </p>
          </div>

          <div className="flex gap-2">
            <Button size="sm" onClick={addNewItem} data-testid="confirm-add-item">Create Item</Button>
            <Button size="sm" variant="ghost" onClick={() => setAddItemOpen(false)}>Cancel</Button>
          </div>
        </div>
      )}

      {/* Center Selector + Filters */}
      <div className="flex flex-wrap items-center gap-3 bg-white border rounded-lg p-3">
        <div className="flex items-center gap-2">
          <label className="text-sm font-medium text-slate-600">Center:</label>
          <select
            className="border rounded px-3 py-1.5 text-sm font-medium min-w-[220px]"
            value={selectedCenter}
            onChange={e => setSelectedCenter(e.target.value)}
            data-testid="menu-center-select"
          >
            {centers.map(c => (
              <option key={c.code} value={c.code}>{c.code} — {c.name}</option>
            ))}
          </select>
        </div>
        <Badge variant="outline" className="text-xs">
          {currency} ({symbol})
        </Badge>
        {menuData && (
          <Badge variant="secondary" className="text-xs">
            {items.filter(i => i.available).length} available / {items.length} total
          </Badge>
        )}
        <div className="flex-1" />
        <div className="relative">
          <Search className="absolute left-2 top-2 w-4 h-4 text-slate-400" />
          <Input
            placeholder="Search items..."
            className="pl-8 w-56 h-8 text-sm"
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            data-testid="menu-search"
          />
        </div>
        <Button variant="ghost" size="sm" onClick={() => setShowUnavailable(!showUnavailable)} data-testid="toggle-unavailable">
          {showUnavailable ? <Eye className="w-4 h-4 mr-1" /> : <EyeOff className="w-4 h-4 mr-1" />}
          {showUnavailable ? "Hide" : "Show"} Unavailable
        </Button>
      </div>

      {/* Menu Items Table */}
      {loading ? (
        <div className="text-center py-12 text-slate-400">Loading menu...</div>
      ) : !menuData ? (
        <div className="text-center py-12 text-slate-400">Select a center to view menu</div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-12 text-slate-400">No menu items found. Click &quot;Seed Menu Data&quot; to import.</div>
      ) : (
        <div className="bg-white border rounded-lg overflow-hidden" data-testid="menu-items-table">
          {catOrder.map(cat => (
            <div key={cat}>
              {/* Category Header */}
              <div
                className="flex items-center gap-2 px-4 py-2.5 bg-slate-50 border-b cursor-pointer hover:bg-slate-100 transition-colors"
                onClick={() => toggleCat(cat)}
                data-testid={`menu-cat-${cat.replace(/[^a-zA-Z0-9]/g, "-")}`}
              >
                {expandedCats.has(cat) ? <ChevronDown className="w-4 h-4 text-slate-500" /> : <ChevronRight className="w-4 h-4 text-slate-500" />}
                <span className="font-semibold text-sm text-slate-700">{cat}</span>
                <Badge variant="outline" className="text-xs ml-1">{grouped[cat].length}</Badge>
              </div>

              {/* Items */}
              {expandedCats.has(cat) && (
                <div className="divide-y divide-slate-100">
                  {/* Column Headers */}
                  <div className="grid grid-cols-12 gap-2 px-4 py-1.5 text-xs font-medium text-slate-400 uppercase tracking-wide bg-slate-50/50">
                    <div className="col-span-4">Item Name</div>
                    <div className="col-span-2">Serves</div>
                    <div className="col-span-2 text-right">Base Price</div>
                    <div className="col-span-2 text-right">Center Price ({symbol})</div>
                    <div className="col-span-2 text-center">Available</div>
                  </div>
                  {grouped[cat].map(item => {
                    const ek = item.id || item.name;
                    const edited = editedPrices[ek] || {};
                    const currentBase = edited.base_price !== undefined ? edited.base_price : item.base_price;
                    const currentPrice = edited.price !== undefined ? edited.price : item.center_price;
                    const currentAvail = edited.available !== undefined ? edited.available : item.available;
                    const isEdited = edited.price !== undefined || edited.available !== undefined || edited.base_price !== undefined;

                    return (
                      <div
                        key={ek}
                        className={`grid grid-cols-12 gap-2 px-4 py-2 items-center text-sm ${!currentAvail ? "opacity-50 bg-red-50/30" : ""} ${isEdited ? "bg-yellow-50" : ""}`}
                        data-testid={`menu-item-${item.name.replace(/[^a-zA-Z0-9]/g, "-")}`}
                      >
                        <div className="col-span-4 flex items-center gap-2">
                          <span className={`w-2 h-2 rounded-full ${item.is_veg ? "bg-green-500" : "bg-red-500"}`} />
                          <button type="button" onClick={() => item.id && costMeta && setCostItem({ menu_item_id: item.id, name: item.name })}
                            className="font-medium text-slate-700 truncate text-left hover:text-orange-700 hover:underline"
                            title="Recipe costing & price details" data-testid={`menu-item-name-${item.name.replace(/[^a-zA-Z0-9]/g, "-")}`}>{item.name}</button>
                          {/* iter181 — Available in N Centres chip */}
                          {(() => {
                            const sum = availSummary[item.name];
                            const n = sum?.available_count ?? item.available_count ?? null;
                            const tot = sum?.total_active_centers ?? item.total_active_centers ?? null;
                            if (n === null || tot === null) return null;
                            const tone = n === 0 ? "bg-red-100 text-red-700 hover:bg-red-200"
                                          : n === tot ? "bg-emerald-100 text-emerald-700 hover:bg-emerald-200"
                                          : "bg-amber-100 text-amber-700 hover:bg-amber-200";
                            return (
                              <button
                                type="button"
                                onClick={() => openAvailModal(item)}
                                className={`ml-1 inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium transition-colors ${tone}`}
                                title="Manage centre availability"
                                data-testid={`avail-chip-${item.name.replace(/[^a-zA-Z0-9]/g, "-")}`}
                              >
                                <Store className="w-3 h-3" />
                                Available in {n}/{tot}
                              </button>
                            );
                          })()}
                        </div>
                        <div className="col-span-2 text-slate-500 text-xs">{item.serves || "-"}</div>
                        <div className="col-span-2 text-right text-slate-400 text-xs">
                          {costMeta?.permissions?.can_edit_price ? (
                            <Input type="number" step="0.01" className={`h-7 text-right text-xs w-20 ml-auto ${edited.base_price !== undefined ? "border-yellow-400 bg-yellow-50" : ""}`}
                              value={currentBase} onChange={e => handlePriceChange(ek, "base_price", e.target.value)}
                              data-testid={`base-price-input-${item.name.replace(/[^a-zA-Z0-9]/g, "-")}`} />
                          ) : `${symbol}${item.base_price}`}
                        </div>
                        <div className="col-span-2 text-right">
                          <Input
                            type="number"
                            step="0.01"
                            className={`h-7 text-right text-sm w-24 ml-auto ${isEdited ? "border-yellow-400 bg-yellow-50" : ""}`}
                            value={currentPrice}
                            onChange={e => handlePriceChange(ek, "price", e.target.value)}
                            data-testid={`price-input-${item.name.replace(/[^a-zA-Z0-9]/g, "-")}`}
                          />
                        </div>
                        <div className="col-span-2 flex justify-center">
                          <button
                            className={`px-3 py-1 rounded text-xs font-medium transition-colors ${currentAvail ? "bg-green-100 text-green-700 hover:bg-green-200" : "bg-red-100 text-red-700 hover:bg-red-200"}`}
                            onClick={() => handlePriceChange(ek, "available", !currentAvail)}
                            data-testid={`avail-toggle-${item.name.replace(/[^a-zA-Z0-9]/g, "-")}`}
                          >
                            {currentAvail ? "Yes" : "No"}
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {costItem && costMeta && (
        <CostingEditDialog open={!!costItem} onClose={() => setCostItem(null)} token={session.token} center={selectedCenter}
          item={costItem} meta={costMeta} channel="dine_in" onSaved={loadMenu} />
      )}
      <DuplicatesDialog open={dupOpen} onClose={() => setDupOpen(false)} token={session?.token} onDone={loadMenu} />

      {/* iter181 — Availability Modal */}
      <Dialog open={!!availModalItem} onOpenChange={(o) => { if (!o) closeAvailModal(); }}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-hidden flex flex-col" data-testid="avail-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Store className="w-4 h-4 text-blue-600" />
              Manage Centre Availability — {availModalItem?.name}
            </DialogTitle>
          </DialogHeader>

          {availLoading ? (
            <div className="text-center py-8 text-slate-400">Loading centres...</div>
          ) : (
            <>
              {/* Bulk action bar */}
              <div className="flex flex-wrap items-center gap-2 border-b pb-3 mb-2 text-xs">
                <span className="text-slate-500 mr-1">Quick actions:</span>
                <Button size="sm" variant="outline"
                  data-testid="avail-apply-all-india"
                  onClick={() => bulkAvailToggle(
                    availRows.filter(r => (r.country || "India").toLowerCase() === "india")
                                .map(r => r.center_code), true)}>
                  <CheckCheck className="w-3.5 h-3.5 mr-1" /> Apply to all India Centres
                </Button>
                <Button size="sm" variant="outline"
                  data-testid="avail-apply-all-active"
                  onClick={() => bulkAvailToggle(availRows.map(r => r.center_code), true)}>
                  <CheckCheck className="w-3.5 h-3.5 mr-1" /> Enable Everywhere
                </Button>
                <Button size="sm" variant="outline"
                  data-testid="avail-remove-all"
                  className="text-red-600 hover:text-red-700"
                  onClick={() => bulkAvailToggle(availRows.map(r => r.center_code), false)}>
                  <X className="w-3.5 h-3.5 mr-1" /> Remove From All
                </Button>
                <div className="flex-1" />
                <span className="text-slate-500">
                  {availRows.filter(r => getAvailFieldValue(r, "available")).length}/{availRows.length} enabled
                </span>
              </div>

              {/* Centres list */}
              <div className="overflow-y-auto -mx-2 px-2" style={{ maxHeight: "50vh" }}>
                {(() => {
                  const byCountry = {};
                  for (const r of availRows) {
                    const k = r.country || "India";
                    (byCountry[k] ||= []).push(r);
                  }
                  return Object.keys(byCountry).sort().map((country) => (
                    <div key={country} className="mb-3">
                      <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-400 mb-1">
                        {country} — {byCountry[country].length} centres
                      </div>
                      <div className="space-y-1">
                        {byCountry[country].map((row) => {
                          const avail = getAvailFieldValue(row, "available");
                          const price = getAvailFieldValue(row, "price");
                          const isDirty = availDirty[row.center_code] !== undefined;
                          return (
                            <div key={row.center_code}
                              className={`grid grid-cols-12 items-center gap-2 rounded border px-3 py-2 text-sm ${
                                isDirty ? "bg-yellow-50 border-yellow-300"
                                        : avail ? "bg-white" : "bg-slate-50 opacity-70"}`}
                              data-testid={`avail-row-${row.center_code}`}>
                              <div className="col-span-5">
                                <div className="font-medium text-slate-700">{row.center_code}</div>
                                <div className="text-[11px] text-slate-500 truncate">{row.center_name}</div>
                              </div>
                              <div className="col-span-3 flex items-center gap-2">
                                <span className="text-[11px] text-slate-400">{row.currency_symbol || "₹"}</span>
                                <Input type="number" step="0.01"
                                  className="h-7 text-right text-xs"
                                  disabled={!avail}
                                  value={price ?? ""}
                                  onChange={(e) => setAvailField(row.center_code, "price", e.target.value)}
                                  data-testid={`avail-price-${row.center_code}`}
                                />
                              </div>
                              <div className="col-span-4 flex justify-end">
                                <button
                                  type="button"
                                  onClick={() => setAvailField(row.center_code, "available", !avail)}
                                  className={`px-3 py-1 rounded text-xs font-medium transition-colors ${
                                    avail ? "bg-green-100 text-green-700 hover:bg-green-200"
                                          : "bg-red-100 text-red-700 hover:bg-red-200"}`}
                                  data-testid={`avail-toggle-modal-${row.center_code}`}
                                >
                                  {avail ? "Available" : "Not Available"}
                                </button>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  ));
                })()}
              </div>
            </>
          )}

          <DialogFooter className="mt-2 border-t pt-3">
            <div className="flex-1 text-[11px] text-slate-500">
              Soft-disable only — historical prices are preserved. Every change is audit-logged.
            </div>
            <Button variant="outline" onClick={closeAvailModal} data-testid="avail-cancel-btn">Cancel</Button>
            <Button
              onClick={saveAvailChanges}
              disabled={availSaving || Object.keys(availDirty).length === 0}
              data-testid="avail-save-btn"
              className="bg-blue-600 hover:bg-blue-700">
              <Save className="w-4 h-4 mr-1" />
              {availSaving ? "Saving..." : `Save ${Object.keys(availDirty).length} Change${Object.keys(availDirty).length === 1 ? "" : "s"}`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
